import { useCallback, useMemo, useRef, useState } from 'react';

import { generateAiReachoutMessageClient } from '@/features/generate-ai-reachout-message/api/client';
import type { RjBulkUsageResponse } from '@/clients/CampaignsClient/CampaignsClient.types';
import { rjBulkUsageQueryKey } from 'shared/legacy/hooks/useGetRjBulkUsageQuery';
import { OutreachOrigin } from '@/shared/lib/outreach-origin';
import { queryClient } from 'config/queryClient';
import { getSchedulerType, type InterviewType } from '@/features/nylas-calendar-manage-connection-modal';

export type BulkSendItem = {
  esPersonId: string;
  candidateName: string;
  campaignId: number;
  improvedJobId: number;
  schedulerType?: InterviewType;
  isAutoSendInterviewInviteEnabled?: boolean;
  subject?: string;
  message?: string;
  flow?: 'email' | 'indeed';
};

export type BulkSendSlotStatus = 'pending' | 'loading' | 'success' | 'error';

export type BulkSendSlot = {
  esPersonId: string;
  candidateName: string;
  status: BulkSendSlotStatus;
  error: Error | null;
  flow?: 'email' | 'indeed';
};

export type BulkSendProgress = {
  total: number;
  sent: number;
  failed: number;
  pending: number;
};

const EMPTY_MAP = new Map<string, BulkSendSlot>();

const bumpRjBulkUsageCache = () => {
  queryClient.setQueryData<RjBulkUsageResponse | undefined>(rjBulkUsageQueryKey, (prev) =>
    prev == null ? prev : { ...prev, sent_count: prev.sent_count + 1 },
  );
};

export function useBulkSendPipeline(concurrency = 3) {
  const [slots, setSlots] = useState<Map<string, BulkSendSlot>>(EMPTY_MAP);
  const queueRef = useRef<BulkSendItem[]>([]);
  const itemsMapRef = useRef<Map<string, BulkSendItem>>(new Map());
  const isRunningRef = useRef(false);
  const initialCountRef = useRef(0);
  const onSuccessRef = useRef<((esPersonId: string) => void) | null>(null);

  const updateSlot = useCallback((esPersonId: string, patch: Partial<BulkSendSlot>) => {
    setSlots((prev) => {
      const next = new Map(prev);
      const current = next.get(esPersonId);
      if (current) {
        next.set(esPersonId, { ...current, ...patch });
      }
      return next;
    });
  }, []);

  const removeSlot = useCallback((esPersonId: string) => {
    setSlots((prev) => {
      const next = new Map(prev);
      next.delete(esPersonId);
      return next;
    });
  }, []);

  const processNext = useCallback(async () => {
    const item = queueRef.current.shift();
    if (!item) return;

    updateSlot(item.esPersonId, { status: 'loading' });

    try {
      await generateAiReachoutMessageClient.startRjCommunicationPipeline({
        campaign_id: item.campaignId,
        es_person_id: item.esPersonId,
        ...(item.subject ? { subject: item.subject } : {}),
        ...(item.message ? { message: item.message } : {}),
        improved_job_id: item.improvedJobId,
        origin: OutreachOrigin.rjCommunicationBulk,
        scheduler_type: item.schedulerType != null ? getSchedulerType(item.schedulerType) : undefined,
        schedule_interview: item.isAutoSendInterviewInviteEnabled,
      });
      bumpRjBulkUsageCache();
      updateSlot(item.esPersonId, { status: 'success' });
      onSuccessRef.current?.(item.esPersonId);
    } catch (err) {
      updateSlot(item.esPersonId, { status: 'error', error: err as Error });
    }

    await processNext();
  }, [updateSlot, removeSlot]);

  const send = useCallback(
    (items: BulkSendItem[], onSuccess?: (esPersonId: string) => void) => {
      if (isRunningRef.current) return;
      isRunningRef.current = true;
      initialCountRef.current = items.length;
      onSuccessRef.current = onSuccess ?? null;

      const initial = new Map<string, BulkSendSlot>();
      for (const item of items) {
        initial.set(item.esPersonId, {
          esPersonId: item.esPersonId,
          candidateName: item.candidateName,
          status: 'pending',
          error: null,
          flow: item.flow ?? 'email',
        });
        itemsMapRef.current.set(item.esPersonId, item);
      }
      setSlots(initial);

      queueRef.current = [...items];
      const workerCount = Math.min(concurrency, items.length);
      for (let i = 0; i < workerCount; i++) {
        processNext();
      }
    },
    [concurrency, processNext],
  );

  const retrySend = useCallback(
    async (esPersonId: string) => {
      const item = itemsMapRef.current.get(esPersonId);
      if (!item) return;

      updateSlot(esPersonId, { status: 'loading', error: null });

      try {
        await generateAiReachoutMessageClient.startRjCommunicationPipeline({
          campaign_id: item.campaignId,
          es_person_id: item.esPersonId,
          subject: item.subject,
          message: item.message,
          improved_job_id: item.improvedJobId,
          origin: OutreachOrigin.rjCommunicationBulk,
          scheduler_type: item.schedulerType != null ? getSchedulerType(item.schedulerType) : undefined,
          schedule_interview: item.isAutoSendInterviewInviteEnabled,
        });
        bumpRjBulkUsageCache();
        updateSlot(esPersonId, { status: 'success' });
        onSuccessRef.current?.(esPersonId);
      } catch (err) {
        updateSlot(esPersonId, { status: 'error', error: err as Error });
      }
    },
    [updateSlot],
  );

  const reset = useCallback(() => {
    queueRef.current = [];
    itemsMapRef.current = new Map();
    isRunningRef.current = false;
    initialCountRef.current = 0;
    onSuccessRef.current = null;
    setSlots(EMPTY_MAP);
  }, []);

  const progress = useMemo<BulkSendProgress>(() => {
    const values = Array.from(slots.values());
    const sent = values.filter((s) => s.status === 'success').length;
    const failed = values.filter((s) => s.status === 'error').length;
    const pending = values.filter((s) => s.status === 'pending' || s.status === 'loading').length;
    return { total: initialCountRef.current, sent, failed, pending };
  }, [slots]);

  const isSending = progress.pending > 0;
  const hasFailed = progress.failed > 0 && !isSending;
  const isComplete = initialCountRef.current > 0 && progress.pending === 0;

  return { slots, progress, isSending, isComplete, hasFailed, send, retrySend, reset };
}
